import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getCurrentUser } from "../services/pocketbase";

const KONAMI_CODE = [
  "ArrowUp",
  "ArrowUp",
  "ArrowDown",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowLeft",
  "ArrowRight",
  "KeyB",
  "KeyA",
];

const KonamiCode = () => {
  const [keysPressed, setKeysPressed] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [snake, setSnake] = useState([{ x: 10, y: 10 }]);
  const [food, setFood] = useState({ x: 5, y: 5 });
  const [direction, setDirection] = useState("RIGHT");
  const [speed, setSpeed] = useState(180); // Slower speed (was 150)
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [congratsOpen, setCongratsOpen] = useState(false);
  const [roleAssigned, setRoleAssigned] = useState(false);
  const [roleError, setRoleError] = useState(null);

  // Sound effect references
  const pickupSoundRef = useRef(null);
  const winSoundRef = useRef(null);
  const loseSoundRef = useRef(null);

  // Handle key presses for Konami code detection
  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.code;

      // For game controls when game is active
      if (gameStarted) {
        if (e.key === "ArrowUp" && direction !== "DOWN") setDirection("UP");
        else if (e.key === "ArrowDown" && direction !== "UP")
          setDirection("DOWN");
        else if (e.key === "ArrowLeft" && direction !== "RIGHT")
          setDirection("LEFT");
        else if (e.key === "ArrowRight" && direction !== "LEFT")
          setDirection("RIGHT");
        return;
      }

      // For Konami code detection
      const newKeysPressed = [...keysPressed, key].slice(-KONAMI_CODE.length);
      setKeysPressed(newKeysPressed);

      // Check if Konami code is entered
      if (
        newKeysPressed.length === KONAMI_CODE.length &&
        newKeysPressed.every((key, i) => key === KONAMI_CODE[i])
      ) {
        setDialogOpen(true);
        setKeysPressed([]);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [keysPressed, gameStarted, direction]);

  // Game loop
  useEffect(() => {
    if (!gameStarted) return;

    const moveSnake = () => {
      if (gameOver) return;

      const head = { ...snake[0] };

      switch (direction) {
        case "UP":
          head.y -= 1;
          break;
        case "DOWN":
          head.y += 1;
          break;
        case "LEFT":
          head.x -= 1;
          break;
        case "RIGHT":
          head.x += 1;
          break;
        default:
          break;
      }

      // Check for collisions with walls
      if (head.x < 0 || head.x >= 25 || head.y < 0 || head.y >= 25) {
        // Increased from 20x20 to 25x25
        setGameOver(true);
        // Play lose sound
        if (loseSoundRef.current) {
          loseSoundRef.current
            .play()
            .catch((e) => console.error("Error playing sound:", e));
        }
        return;
      }

      // Check for collisions with self
      for (let i = 0; i < snake.length; i++) {
        if (head.x === snake[i].x && head.y === snake[i].y) {
          setGameOver(true);
          // Play lose sound
          if (loseSoundRef.current) {
            loseSoundRef.current
              .play()
              .catch((e) => console.error("Error playing sound:", e));
          }
          return;
        }
      }

      // Check if snake eats food
      let newSnake = [head, ...snake];
      if (head.x === food.x && head.y === food.y) {
        // Generate new food
        const newFood = {
          x: Math.floor(Math.random() * 25), // Increased from 20 to 25
          y: Math.floor(Math.random() * 25), // Increased from 20 to 25
        };
        setFood(newFood);

        // Play pickup sound
        if (pickupSoundRef.current) {
          pickupSoundRef.current
            .play()
            .catch((e) => console.error("Error playing sound:", e));
        }

        // Increase score
        const newScore = score + 1;
        setScore(newScore);

        // Check if player reached score of 5
        if (newScore >= 5) {
          // Close the game dialog and open congrats dialog instead
          setDialogOpen(false);
          setCongratsOpen(true);
          setGameStarted(false);
          // Play win sound
          if (winSoundRef.current) {
            winSoundRef.current
              .play()
              .catch((e) => console.error("Error playing sound:", e));
          }

          // Call the function to assign Discord role
          assignDiscordRole();
        }

        // Increase speed slightly
        setSpeed((prevSpeed) => Math.max(prevSpeed - 5, 100)); // Slower minimum speed (was 80)
      } else {
        // Remove tail if no food eaten
        newSnake.pop();
      }

      setSnake(newSnake);
    };

    const gameInterval = setInterval(moveSnake, speed);
    return () => clearInterval(gameInterval);
  }, [snake, food, direction, gameStarted, gameOver, score, speed]);

  // Function to assign Discord role to the user
  const assignDiscordRole = async () => {
    try {
      const user = getCurrentUser();

      // Check if user is logged in and has a Discord ID
      if (!user || !user.discord_id) {
        console.error("User not logged in or missing Discord ID");
        setRoleError(
          "You need to link your Discord account to receive the role reward!"
        );
        return;
      }

      // Define the role ID for the winner role
      const winnerRoleId = "1372208905983234220"; // Using the role ID from your network request

      // Call the new API endpoint to assign the role
      const response = await fetch("/api/discord/assign-role", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          discordId: user.discord_id,
          roleId: winnerRoleId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.text(); // Changed from response.json() to handle non-JSON responses
        throw new Error(errorData || "Failed to assign role");
      }

      // Role assigned successfully
      setRoleAssigned(true);
      setRoleError(null);
    } catch (error) {
      console.error("Error assigning Discord role:", error);
      setRoleError("Failed to assign Discord role: " + error.message);
      setRoleAssigned(false);
    }
  };

  const startGame = () => {
    setSnake([{ x: 12, y: 12 }]); // Adjusted starting position for larger board
    setFood({ x: 5, y: 5 });
    setDirection("RIGHT");
    setGameOver(false);
    setScore(0);
    setSpeed(180); // Slower initial speed (was 150)
    setGameStarted(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setGameStarted(false);
    setGameOver(false);
  };

  const closeCongratsDialog = () => {
    setCongratsOpen(false);
    setRoleAssigned(false);
    setRoleError(null);
  };

  const renderCell = (x, y) => {
    // Check if cell contains snake
    const isSnake = snake.some((segment) => segment.x === x && segment.y === y);
    // Check if cell contains food
    const isFood = food.x === x && food.y === y;

    return (
      <div
        key={`${x}-${y}`}
        style={{
          width: "18px", // Increased from 15px
          height: "18px", // Increased from 15px
          backgroundColor: isSnake
            ? "#4caf50"
            : isFood
            ? "#f44336"
            : "transparent",
          border: "1px solid #333",
        }}
      />
    );
  };

  const renderGameBoard = () => {
    const board = [];
    for (let y = 0; y < 25; y++) {
      // Increased from 20 to 25
      const row = [];
      for (let x = 0; x < 25; x++) {
        // Increased from 20 to 25
        row.push(renderCell(x, y));
      }
      board.push(
        <div key={y} style={{ display: "flex" }}>
          {row}
        </div>
      );
    }
    return board;
  };

  return (
    <>
      {/* Sound effects */}
      <audio ref={pickupSoundRef} src="/sounds/pickup.mp3" preload="auto" />
      <audio ref={winSoundRef} src="/sounds/win.mp3" preload="auto" />
      <audio ref={loseSoundRef} src="/sounds/lose.mp3" preload="auto" />

      {/* Game Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md">
        <DialogTitle>
          Secret Snake Game
          <IconButton
            aria-label="close"
            onClick={closeDialog}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Typography variant="h6">
              {gameOver
                ? "Game Over!"
                : gameStarted
                ? `Score: ${score}`
                : "Press Start to Play"}
            </Typography>
            {!gameStarted && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Use arrow keys to control the snake. Collect 5 red dots to win a
                special Discord role!
              </Typography>
            )}
          </Box>

          {/* Game board */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              border: "2px solid #333",
              backgroundColor: "#222",
              width: "fit-content",
              margin: "0 auto",
            }}
          >
            {/* Render game board here */}
            {renderGameBoard()}
          </Box>

          <Box sx={{ textAlign: "center", mt: 2 }}>
            {!gameStarted ? (
              <Button
                variant="contained"
                color="primary"
                onClick={startGame}
                sx={{ mt: 2 }}
              >
                {gameOver ? "Play Again" : "Start Game"}
              </Button>
            ) : (
              <Typography variant="body2">
                Use arrow keys to move the snake
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Congratulations Dialog */}
      <Dialog open={congratsOpen} onClose={closeCongratsDialog} maxWidth="sm">
        <DialogTitle>
          Congratulations!
          <IconButton
            aria-label="close"
            onClick={closeCongratsDialog}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              You've Won the Secret Game!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Your score: {score}
            </Typography>

            {roleAssigned && (
              <Typography variant="body1" sx={{ color: "success.main", mb: 2 }}>
                🎉 A special Discord role has been added to your account! 🎉
              </Typography>
            )}

            {roleError && (
              <Typography variant="body1" sx={{ color: "error.main", mb: 2 }}>
                {roleError}
              </Typography>
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={closeCongratsDialog}
              sx={{ mt: 2 }}
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default KonamiCode;
